import NiceModal from '@ebay/nice-modal-react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';

import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import Button from 'components/field/Button';
import Image from 'components/global/Image';
import LottiePlayer from 'components/global/LottiePlayer';
import Typography from 'components/global/Typography';

import InternationalProforma from 'pages/Shipments/PrintOrders/InternationalProforma';
import PrintAWB from 'pages/Shipments/PrintOrders/PrintAWB';

import fetchRemainingAmount from 'services/common/fetchRemainingAmount';

import AppLogger from 'utils/AppLogger';

import { faPlus, faPrint } from '@fortawesome/pro-solid-svg-icons';

import DownloadShippingStickersModal from './DownloadShippingStickersModal/DownloadShippingStickersModal';
import * as S from './PriceCalculator.styled';

const ShippingSuccessTab = ({ handleCreateNewShipment, importOrder }) => {
	const { t } = useTranslation();
	const formMethods = useFormContext();
	const { watch } = formMethods;

	const { originAddress, destinationAddress, priceConfig, shipmentResultData = null } = watch();
	const {
		nameAr: deliveryCompanyName,
		estimatedDeliveryDate,
		estimatedPickupDate,
		deliveryTime,
		logo,
		successInstructionList,
		checkAllBranches,
		successMessage
	} = priceConfig;

	const pickupType =
		priceConfig.pickupType === 'PICKUP_BY_DC_OR_BRANCH_DROP_OF'
			? shipmentResultData?.pickupType
			: priceConfig?.pickupType;

	useEffect(() => {
		return () => {
			fetchRemainingAmount();
		};
	}, []);

	return (
		<S.ShippingSuccessCard fullWidth rounded padding={0}>
			<S.ShippingSuccessCardContent>
				<LottiePlayer className="lottie" name={'package-success'} width="auto" height="auto" />

				<S.ShippingSuccessTextContainer>
					<Typography variant="p1" fontWeight="500" noWrap fullWidth align="center">
						<Trans
							i18nKey="yourShipmentHasBeenCreatedSuccessfully"
							components={{
								shipmentNumber: (
									<Button
										component="a"
										href={shipmentResultData?.trackingUrl}
										label={shipmentResultData?.shipmentId}
										variant="blueBorderless"
										css={css`
											display: inline-flex;
											padding: 0 !important;
											text-decoration: underline;

											> .button-text {
												font-size: var(--paragraph1-font-size);
											}
										`}
										target="_blank"
									/>
								)
							}}
						/>
					</Typography>
					<Typography variant="p3" noWrap fullWidth align="center">
						<Trans
							i18nKey={
								successMessage
									? successMessage
									: importOrder
										? 'importOrderShipmentCreatedDroppedOffText'
										: pickupType === 'BRANCH_DROP_OFF'
											? 'priceCalculatorShipmentCreatedDroppedOffText'
											: 'priceCalculatorShipmentCreatedTextNew'
							}
							values={{
								deliveryCompany: t(deliveryCompanyName),
								deliveryDate: dayjs(estimatedDeliveryDate).format('dddd DD.MM.YYYY'),
								pickupDate: dayjs(estimatedPickupDate).format('dddd DD.MM.YYYY'),
								deliveryTime: t(deliveryTime)
							}}
							components={{
								blackText: <S.BlackText />,
								red: <Typography fontWeight="500" color="var(--red)" />,
								bold: <Typography fontWeight="500" />
							}}
						/>
					</Typography>
				</S.ShippingSuccessTextContainer>

				<S.ShippingSuccessGuidesContainer>
					<S.ShippingSuccessGuidesHeader>
						<S.ShippingFromContainer>
							<Typography color="var(--content-3)">from</Typography>
							<Typography fontWeight="500">
								{[t(originAddress.cityName), originAddress.countryName]
									.filter((item) => !!item)
									.join(', ')}
							</Typography>
						</S.ShippingFromContainer>
						<S.ShippingCompanyLogoWrapper>
							<Image url={logo} width="6rem" height="6rem" />
							<S.ShippingCompanyLogoLeftDot />
							<S.ShippingCompanyLogoDashedLine />
							<S.ShippingCompanyLogoRightDot />
						</S.ShippingCompanyLogoWrapper>
						<S.ShippingToContainer>
							<Typography color="var(--content-3)">to</Typography>
							<Typography fontWeight="500">
								{[t(destinationAddress.cityName), destinationAddress.countryName]
									.filter((item) => !!item)
									.join(', ')}
							</Typography>
						</S.ShippingToContainer>
					</S.ShippingSuccessGuidesHeader>
					{successInstructionList?.length > 0 && (
						<S.ShippingSuccessGuidesContent>
							<Typography variant="p2" fontWeight="500">
								prepareYourOrderForShipping
							</Typography>
							<S.ShippingSuccessGuides>
								{successInstructionList
									?.sort((a, b) => a.priority - b.priority)
									.map(({ instructionType }, i) => {
										if (instructionType === 'packOrder') {
											return (
												<S.ShippingSuccessGuideItem key={i}>
													<Image
														url="https://storage.googleapis.com/tryoto-public/svg/pack-order.svg"
														width="6rem"
														height="6rem"
													/>
													<S.ShippingSuccessGuideItemRightContainer>
														<S.ShippingSuccessGuideItemTextContainer>
															<Typography fontWeight="500">packOrder</Typography>
															<Typography color="var(--content-2)" noWrap>
																packOrderGuideDescription
															</Typography>
														</S.ShippingSuccessGuideItemTextContainer>
														<S.ShippingSuccessGuideItemLinkContainer>
															<Button
																label="downloadStickers"
																variant="blueBorderless"
																css={css`
																	padding: 0 !important;
																	text-decoration: underline;

																	> .button-text {
																		font-weight: 500;
																	}
																`}
																onClick={() => {
																	NiceModal.show(DownloadShippingStickersModal);
																}}
															/>
														</S.ShippingSuccessGuideItemLinkContainer>
													</S.ShippingSuccessGuideItemRightContainer>
												</S.ShippingSuccessGuideItem>
											);
										} else if (instructionType === 'printAirWaybill') {
											return (
												<S.ShippingSuccessGuideItem key={i}>
													<Image
														url="https://storage.googleapis.com/tryoto-public/svg/print-awb.svg"
														width="6rem"
														height="6rem"
													/>
													<S.ShippingSuccessGuideItemRightContainer>
														<S.ShippingSuccessGuideItemTextContainer>
															<Typography fontWeight="500">
																printShippingLabelAWB
															</Typography>
															<Typography color="var(--content-2)" noWrap>
																printShippingLabelAWBGuideDescription
															</Typography>
														</S.ShippingSuccessGuideItemTextContainer>
														<S.ShippingSuccessGuideItemLinkContainer>
															<Button
																label="printShippingLabelAWB"
																variant="blueBorderless"
																css={css`
																	padding: 0 !important;
																	text-decoration: underline;

																	> .button-text {
																		font-weight: 500;
																	}
																`}
																onClick={() => {
																	NiceModal.show(PrintAWB, {
																		ids: [watch('orderID')]
																	}).then((resolved) => {
																		const { internationalOrders = null } = resolved;

																		if (internationalOrders?.length > 0) {
																			NiceModal.show(InternationalProforma, {
																				ids: internationalOrders
																			});
																		}
																	});
																}}
															/>
														</S.ShippingSuccessGuideItemLinkContainer>
													</S.ShippingSuccessGuideItemRightContainer>
												</S.ShippingSuccessGuideItem>
											);
										} else if (
											instructionType === 'dropOffYourPackages' &&
											pickupType === 'BRANCH_DROP_OFF'
										) {
											return (
												<S.ShippingSuccessGuideItem key={i}>
													<Image
														url="https://storage.googleapis.com/tryoto-public/svg/drop-off-your-package.svg"
														width="6rem"
														height="6rem"
													/>
													<S.ShippingSuccessGuideItemRightContainer>
														<S.ShippingSuccessGuideItemTextContainer>
															<Typography fontWeight="500">dropOffYourPackage</Typography>
															<Typography color="var(--content-2)" noWrap>
																dropOffYourPackageGuideDescription
															</Typography>
														</S.ShippingSuccessGuideItemTextContainer>
														<S.ShippingSuccessGuideItemLinkContainer>
															<Button
																label="viewBranches"
																variant="blueBorderless"
																css={css`
																	padding: 0 !important;
																	text-decoration: underline;

																	> .button-text {
																		font-weight: 500;
																	}
																`}
																component="a"
																href={checkAllBranches}
																target="_blank"
															/>
														</S.ShippingSuccessGuideItemLinkContainer>
													</S.ShippingSuccessGuideItemRightContainer>
												</S.ShippingSuccessGuideItem>
											);
										} else {
											AppLogger.error(`Unknown instructionType: ${instructionType}`);
											return undefined;
										}
									})}
								<S.ShippingSuccessGuidesContentDashedLine />
							</S.ShippingSuccessGuides>
						</S.ShippingSuccessGuidesContent>
					)}
				</S.ShippingSuccessGuidesContainer>
			</S.ShippingSuccessCardContent>
			<S.ShippingSuccessButtonContainer>
				<Button icon={faPlus} label="createNewShipment" variant="outline" onClick={handleCreateNewShipment} />

				<Button
					icon={faPrint}
					label="printShippingLabelAWB"
					onClick={() => {
						NiceModal.show(PrintAWB, {
							ids: [watch('orderID')]
						}).then((resolved) => {
							const { internationalOrders = null } = resolved;

							if (internationalOrders?.length > 0) {
								NiceModal.show(InternationalProforma, {
									ids: internationalOrders
								});
							}
						});
					}}
				/>
			</S.ShippingSuccessButtonContainer>
		</S.ShippingSuccessCard>
	);
};

export default ShippingSuccessTab;
